import React, { useEffect, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import gql from 'graphql-tag';
import { useTranslation } from 'react-i18next';
import { useRootStore } from 'store';
import { TabContextTitle } from '../components/tab-context-title';
import { FileRepoField } from 'shared-pages/components/filerepo-field';
import { FileRepoFieldList } from 'shared-pages/components/filerepo-field-list';
import { apiBase } from 'shared-pages/common/api-helpfunc';
import {
  FieldInput,
  FieldCheckBox,
  FieldMaskInput,
  FormCol6,
  FormRow,
  FormCol12,
  CustomSelectComponent,
  FsProps,
  FormCol,
  FieldTextArea,
  FormCol2,
  FormCol3,
  FormCol4,
  FieldBooleanTriState,
  FieldDatepicker,
  FieldTickBox,
  toastFailure,
} from 'motion-components';
import { Badge, Button, Col, Spinner } from 'react-bootstrap';
import { useFormikContext } from 'formik';
import { useFieldDisabledByStatus } from '../services';
import { FieldSelectTechnicianAssessment } from '../components/fs-technician-assessment';
import { FieldSelectChemistry } from '../components/fs-chemistry';
import { FieldSelectBmsCharacterization } from '../components/fs-bms-characterization';
import { FieldSelectGeometry } from '../components/fs-select-geometry';
import { observer } from 'mobx-react-lite';
import AiExtractionDecoration from '../components/ai-extraction-decoration';
import { FieldSelectPackagingCondition } from '../components/fs-packaging-condition';
import { gqlEncodePdfAccessToken } from '../../queries';

export const OrderRequestTab = observer(({ data, xrbData }: { data: any; xrbData: any }) => {
  const { appStore } = useRootStore();
  const { t } = useTranslation();
  const client = useApolloClient();
  const { values, setFieldValue } = useFormikContext<any>();
  const fieldDisabled = useFieldDisabledByStatus();
  const [isPrintingPdf, setIsPrintingPdf] = useState(false);

  const pieceCount0 = Math.max(0, parseInt(values.xrbData?.pieceCount, 10) || 0);
  const pieceCount = pieceCount0 > 10 ? 10 : pieceCount0 < 1 ? 1 : pieceCount0;
  const pieceDetails = values.xrbData?.pieceDetails ?? [];
  const orderId = Number(data?.id ?? values?.id);
  const canPrintOrderRequestPdf = Number.isInteger(orderId) && orderId > 0;

  async function handlePrintOrderRequestPdf(): Promise<void> {
    if (!canPrintOrderRequestPdf || isPrintingPdf) return;
    try {
      setIsPrintingPdf(true);
      const payload = JSON.stringify({
        type: 'collection-order-request',
        args: { id: orderId },
        expirationDate: new Date(Date.now() + 10 * 60 * 1000).toISOString(),
      });
      const ret = await client.query({
        query: gqlEncodePdfAccessToken,
        variables: { key: payload },
        fetchPolicy: 'no-cache',
      });
      const token = ret?.data?.encodeKey;
      if (!token) {
        throw new Error('Missing PDF access token');
      }

      const url = `${apiBase()}/pdf/access?token=${encodeURIComponent(token)}`;
      const win = window.open(url, '_blank');
      win?.focus();
    } catch (error) {
      console.error('Failed to open request PDF:', error);
      toastFailure(t('Αποτυχία δημιουργίας PDF αίτησης.'));
    } finally {
      setIsPrintingPdf(false);
    }
  }

  // When certified packaging is not available, clear the code field
  useEffect(() => {
    if (values.xrbData?.certifiedPackagingIsAvailable === false) {
      setFieldValue('xrbData.certifiedPackagingCode', '');
    }
  }, [values.xrbData?.certifiedPackagingIsAvailable, setFieldValue]);

  // Keep pieceDetails array length in sync with pieceCount
  useEffect(() => {
    if (pieceCount <= 0) return;
    if (pieceDetails.length === pieceCount) return;
    if (pieceDetails.length < pieceCount) {
      const next = [...pieceDetails];
      while (next.length < pieceCount) {
        next.push({
          pieceIndex: next.length + 1,
          daysOutOfOperation: null,
          daysNotMoved: null,
          chargeLevel: null,
          chargeLevelDate: null,
        });
      }
      setFieldValue('xrbData.pieceDetails', next);
    } else {
      setFieldValue('xrbData.pieceDetails', pieceDetails.slice(0, pieceCount));
    }
  }, [pieceCount, pieceDetails.length, setFieldValue]);

  return (
    <div>
      <TabContextTitle label={t('Στοιχεία Αίτησης')} badge="1" />
      <div className="mb-3 d-flex">
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={handlePrintOrderRequestPdf}
          disabled={!canPrintOrderRequestPdf || isPrintingPdf}
          title={
            !canPrintOrderRequestPdf ? t('Αποθήκευσε πρώτα την αίτηση για εκτύπωση.') : undefined
          }
        >
          {isPrintingPdf ? (
            <span className="me-2">
              <Spinner animation="border" size="sm" />
            </span>
          ) : null}
          {t('Εκτύπωση Αίτησης (PDF)')}
        </Button>
      </div>

      <h5 className="form-group-title mb-3">{t('Α1. Τεχνικές πληροφορίες για την μπαταρία')}</h5>
      <FormRow>
        <FieldInput
          as={FormCol6}
          label={t('Πλήθος όμοιων μπαταριών προς συλλογή (τμχ)')}
          helpText={t(
            'Πλήθος τεμαχίων όμοιων μπαταριών προς συλλογή με την προϋπόθεση ότι (α) είναι στην ίδια κατάσταση, (β) έχουν το ίδιο βάρος/τμχ, (γ) έχουν την ίδια χημική σύσταση και (δ) φέρουν την ίδια συσκευασία.',
          )}
          name="xrbData.pieceCount"
          placeholder=""
          disabled={fieldDisabled['xrbData.pieceCount']}
        />
      </FormRow>
      <FormRow>
        <FormCol6>
          <FileRepoField
            as={FormCol12}
            label="Δελτίο Δεδομένων Ασφαλείας (SDS)"
            fileIdFieldName="xrbData.msdsFileId"
            fileRec={xrbData?.msdsFileRep}
            canEdit={!fieldDisabled['xrbData.msdsFileId']}
          />
        </FormCol6>
        <FormCol6>
          <FileRepoField
            as={FormCol12}
            label="Φύλλο Τεχνικών Χαρακτηριστικών της Μπαταρίας"
            fileIdFieldName="xrbData.techSpecFileId"
            fileRec={xrbData?.techSpecFileRep}
            canEdit={!fieldDisabled['xrbData.techSpecFileId']}
          />
        </FormCol6>
      </FormRow>
      <FormRow>
        <FormCol6>
          <FileRepoField
            as={FormCol12}
            label="Αναφορά αξιολόγησης του Διαγνωστικού Ελέγχου (BMS)"
            helpText="Αναφορά αξιολόγησης του Διαγνωστικού Ελέγχου (health report του BMS ή άλλου διαγνωστικού μέσου)"
            fileIdFieldName="xrbData.bmsFileId"
            fileRec={xrbData?.bmsFileRep}
            canEdit={!fieldDisabled['xrbData.bmsFileId']}
          />
        </FormCol6>
      </FormRow>
      <FormRow>
        <AiExtractionDecoration
          as={FormCol6}
          aiFileDataExtractionObject={values.xrbData?.aiFileDataExtractionObject?.manufacturer}
        >
          <FieldInput
            // as={FormCol6}
            label={t('Κατασκευαστής Μπαταρίας')}
            name="xrbData.manufacturer"
            placeholder=""
            disabled={fieldDisabled['xrbData.manufacturer']}
          />
        </AiExtractionDecoration>

        <AiExtractionDecoration
          as={FormCol6}
          aiFileDataExtractionObject={values.xrbData?.aiFileDataExtractionObject?.partCode}
        >
          <FieldInput
            // as={FormCol6}
            label={t('Κωδικός μπαταρίας (model, part / serial number)')}
            name="xrbData.partCode"
            placeholder=""
            disabled={fieldDisabled['xrbData.partCode']}
          />
        </AiExtractionDecoration>
      </FormRow>
      <FormRow>
        <AiExtractionDecoration
          as={FormCol6}
          aiFileDataExtractionObject={values.xrbData?.aiFileDataExtractionObject?.chemistry}
        >
          <FieldSelectChemistry
            // as={FormCol6}
            as={FormCol12}
            name="xrbData.chemistry"
            label={t('Χημική σύσταση της μπαταρίας (chemistry)')}
            disabled={fieldDisabled['xrbData.chemistry']}
          />
        </AiExtractionDecoration>

        <AiExtractionDecoration
          as={FormCol6}
          aiFileDataExtractionObject={values.xrbData?.aiFileDataExtractionObject?.cellGeometry}
        >
          <FieldSelectGeometry
            as={FormCol12}
            name="xrbData.cellGeometry"
            label={t('Γεωμετρία κυψελών')}
            disabled={fieldDisabled['xrbData.cellGeometry']}
          />
        </AiExtractionDecoration>
      </FormRow>
      <FormRow>
        <AiExtractionDecoration
          as={FormCol6}
          aiFileDataExtractionObject={values.xrbData?.aiFileDataExtractionObject?.nominalCapacity}
        >
          <FieldInput
            // as={FormCol6}
            label={t('Ονομαστική χωρητικότητα (Ah)')}
            name="xrbData.nominalCapacity"
            placeholder=""
            disabled={fieldDisabled['xrbData.nominalCapacity']}
          />
        </AiExtractionDecoration>
        <AiExtractionDecoration
          as={FormCol6}
          aiFileDataExtractionObject={values.xrbData?.aiFileDataExtractionObject?.nominalVoltage}
        >
          <FieldInput
            // as={FormCol6}
            label={t('Ονομαστική τάση (V)')}
            name="xrbData.nominalVoltage"
            placeholder=""
            disabled={fieldDisabled['xrbData.nominalVoltage']}
          />
        </AiExtractionDecoration>
      </FormRow>
      <FormRow>
        <AiExtractionDecoration
          as={FormCol6}
          aiFileDataExtractionObject={
            values.xrbData?.aiFileDataExtractionObject?.nominalEnergyEfficiency
          }
        >
          <FieldInput
            // as={FormCol6}
            label={t('Ονομαστική ικανότητα / αποθήκευσης ενέργειας (kWh)')}
            name="xrbData.nominalEnergyEfficiency"
            placeholder=""
            disabled={fieldDisabled['xrbData.nominalEnergyEfficiency']}
          />
        </AiExtractionDecoration>

        <AiExtractionDecoration
          as={FormCol6}
          aiFileDataExtractionObject={values.xrbData?.aiFileDataExtractionObject?.batteryWeight}
        >
          <FieldInput
            // as={FormCol6}
            label={t('Βάρος μπαταρίας (kg/τμχ)')}
            name="xrbData.batteryWeight"
            placeholder=""
            disabled={fieldDisabled['xrbData.batteryWeight']}
          />
        </AiExtractionDecoration>
      </FormRow>
      <FormRow>
        <AiExtractionDecoration
          as={FormCol3}
          aiFileDataExtractionObject={values.xrbData?.aiFileDataExtractionObject?.batteryLength}
        >
          <FieldInput
            label={t('Μήκος μπαταρίας (cm)')}
            name="xrbData.batteryLength"
            placeholder=""
            disabled={fieldDisabled['xrbData.batteryLength']}
          />
        </AiExtractionDecoration>

        <AiExtractionDecoration
          as={FormCol3}
          aiFileDataExtractionObject={values.xrbData?.aiFileDataExtractionObject?.batteryWidth}
        >
          <FieldInput
            label={t('Πλάτος μπαταρίας (cm)')}
            name="xrbData.batteryWidth"
            placeholder=""
            disabled={fieldDisabled['xrbData.batteryWidth']}
          />
        </AiExtractionDecoration>

        <AiExtractionDecoration
          as={FormCol3}
          aiFileDataExtractionObject={values.xrbData?.aiFileDataExtractionObject?.batteryHeight}
        >
          <FieldInput
            label={t('Ύψος μπαταρίας (cm)')}
            name="xrbData.batteryHeight"
            placeholder=""
            disabled={fieldDisabled['xrbData.batteryHeight']}
          />
        </AiExtractionDecoration>
      </FormRow>

      {/* Ημέρες & SoC ανά τεμάχιο (stacked rows) */}
      {pieceCount >= 1 && (
        <>
          <h6 className="form-group-title mt-3 mb-2">{t('Α1.1 Στοιχεία ανά τεμάχιο μπαταρίας')}</h6>
          {Array.from({ length: pieceCount }, (_, i) => (
            <FormRow key={i} className="mb-2">
              <Col className="col-auto d-flex align-items-end pb-2 pe-1">
                <Badge bg="secondary" text="light" className="fs-6">
                  {i + 1}
                </Badge>
              </Col>
              <FieldInput
                as={FormCol3}
                label={t('Αριθμός ημερών που η μπαταρία είναι εκτός λειτουργίας')}
                helpText={t(
                  'Το αίτημα συλλογής υποβάλλεται μετά το πέρς πέντε (3) ημερών που η μπαταρία βρίσκεται εκτός λειτουργίας.',
                )}
                name={`xrbData.pieceDetails[${i}].daysOutOfOperation`}
                placeholder=""
                disabled={fieldDisabled[`xrbData.pieceDetails.daysOutOfOperation`]}
              />
              <FieldInput
                as={FormCol3}
                label={t('Αριθμός ημερών που η μπαταρία δεν έχει μετακινηθεί')}
                name={`xrbData.pieceDetails[${i}].daysNotMoved`}
                placeholder=""
                disabled={fieldDisabled[`xrbData.pieceDetails.daysNotMoved`]}
              />
              <FieldInput
                as={FormCol3}
                label={t('Επίπεδο φόρτισης (%) (SoC)')}
                name={`xrbData.pieceDetails[${i}].chargeLevel`}
                placeholder=""
                disabled={fieldDisabled[`xrbData.pieceDetails[${i}].chargeLevel`]}
              />

              <FieldDatepicker
                as={Col}
                label={t('Ημερομηνία μέτρησης (SoC)')}
                name={`xrbData.pieceDetails[${i}].chargeLevelDate`}
                disabled={fieldDisabled[`xrbData.pieceDetails[${i}].chargeLevelDate`]}
              />
            </FormRow>
          ))}
        </>
      )}

      {/* A2. Προφυλάξεις ασφαλείας */}
      <h5 className="form-group-title mt-3 mb-2">{t('Α2. Προφυλάξεις ασφαλείας')}</h5>
      <FormRow>
        <FieldBooleanTriState
          disabled={fieldDisabled['xrbData.cablesSecured']}
          as={FormCol6}
          label={t('Όλα τα χαλαρά καλώδια έχουν ασφαλισθεί')}
          helpText={t(
            'Όλα τα χαλαρά καλώδια ή σημεία ηλεκτρικής σύνδεσης (ports) της μπαταρίας είναι προστατευμένα από ανεπιθύμητη αγώγιμη σύνδεση/ βραχυκύκλωμα (εφόσον έχει εφαρμογή η ενέργεια αυτή).',
          )}
          helpTriggerLabel={t('Τι σημαίνει αυτό;')}
          name="xrbData.cablesSecured"
        />
        <FieldBooleanTriState
          disabled={fieldDisabled['xrbData.safetyPlugInstalled']}
          as={FormCol6}
          label={t(
            'Οι είσοδοι/ έξοδοι του κυκλώματος ψύξης της μπαταρίας είναι στεγανές και καλυμμένες ',
          )}
          name="xrbData.safetyPlugInstalled"
        />
      </FormRow>
      <FormRow>
        <FieldCheckBox
          as={FormCol6}
          label={t('Διατίθεται πιστοποιημένη συσκευασία')}
          name="xrbData.certifiedPackagingIsAvailable"
          disabled={fieldDisabled['xrbData.certifiedPackagingIsAvailable']}
        />

        {values.xrbData?.certifiedPackagingIsAvailable !== false && (
          <FieldMaskInput
            as={FormCol6}
            name="xrbData.certifiedPackagingCode"
            label={
              <>
                <img
                  src={`${process.env.PUBLIC_URL || ''}/resources/certified-packaging-un-icon.png`}
                  alt=""
                  style={{
                    width: '1.25em',
                    height: '1.25em',
                    marginRight: 6,
                    verticalAlign: 'middle',
                  }}
                />
                {t('Κωδικός πιστοποιημένης συσκευασίας')}
              </>
            }
            helpText={t('Κωδικός πιστοποιημένης συσκευασίας π.χ. 4G/Y345/S/24')}
            mask="**/****/*/**"
            maskChar="_"
            formatChars={{ '*': '[A-Za-z0-9-]' }}
            disabled={fieldDisabled['xrbData.certifiedPackagingCode']}
          />
        )}
      </FormRow>
      <FormRow>
        <FieldSelectPackagingCondition
          as={FormCol6}
          label={t('Κατάσταση συσκευασίας')}
          name="xrbData.packagingCondition"
          placeholder=""
          disabled={fieldDisabled['xrbData.packagingCondition']}
        />
      </FormRow>
      <FormRow>
        <FieldInput
          as={FormCol3}
          label={t('Εσωτερικό Ύψος συσκευασίας (cm)')}
          name="xrbData.packagingHeight"
          placeholder=""
          disabled={fieldDisabled['xrbData.packagingHeight']}
        />

        <FieldInput
          as={FormCol3}
          label={t('Εσωτερικό Μήκος συσκευασίας (cm)')}
          name="xrbData.packagingLength"
          placeholder=""
          disabled={fieldDisabled['xrbData.packagingLength']}
        />
        <FieldInput
          as={FormCol3}
          label={t('Εσωτερικό Πλάτος συσκευασίας (cm)')}
          name="xrbData.packagingWidth"
          placeholder=""
          disabled={fieldDisabled['xrbData.packagingWidth']}
        />
      </FormRow>
      <FormRow>
        <FieldCheckBox
          as={FormCol6}
          label={t('Χρησιμοποιείται η Συσκευασία της Ανταλλακτικής Μπαταρίας')}
          name="xrbData.replacementPackagingIsUsed"
          disabled={fieldDisabled['xrbData.replacementPackagingIsUsed']}
        />
        <FieldCheckBox
          disabled={fieldDisabled['xrbData.packagingReturnRequired']}
          as={FormCol6}
          label={t('Απαιτείται η επιστροφή της συσκευασίας')}
          name="xrbData.packagingReturnRequired"
        />
      </FormRow>

      {/* B. Ιστορικό και οπτικός έλεγχος */}
      <h5 className="form-group-title mt-3 mb-2">{t('B. Ιστορικό και οπτικός έλεγχος')}</h5>
      <FormRow>
        <FieldSelectBmsCharacterization
          as={FormCol6}
          name="xrbData.bmsCharacterization"
          label={t('Χαρακτηρισμός μπαταρίας σύμφωνα με BMS')}
          helpText={
            <>
              Χαρακτηρισμός της μπαταρίας σύμφωνα με την αναφορά του BMS/ διαγνωστικού εργαλείου.
              (1) <strong>Safe for transport</strong> (κατάλληλη για ασφαλή μεταφορά στη συσκευασία
              της ανταλλακτικής μπαταρίας ή αντίστοιχη) (2) <strong>Safe for transport</strong> με
              εξασφάλιση ειδικών απαιτήσεων για τη συσκευασία (πέραν αυτών της ανταλλακτικής
              συσκευασίας) (3) <strong>No available information</strong> (μη διαθέσιμες πληροφορίες)
              (4) <strong>Άλλος χαρακτηρισμός/ ένδειξη:</strong>
            </>
          }
          disabled={fieldDisabled['xrbData.bmsCharacterization']}
        />
        {values.xrbData?.bmsCharacterization === 'other' && (
          <FieldInput
            as={FormCol6}
            label={t('Άλλος χαρακτηρισμός/ ένδειξη')}
            name="xrbData.bmsCharacterizationOther"
            placeholder=""
            disabled={fieldDisabled['xrbData.bmsCharacterizationOther']}
          />
        )}
      </FormRow>
      <h6>Η μπαταρία έχει εμπλακεί ή έχει επηρεαστεί από:</h6>
      <FormRow>
        <FieldCheckBox
          disabled={fieldDisabled['xrbData.carAccident']}
          as={FormCol6}
          label={t('Αυτοκινητιστικό ατύχημα')}
          name="xrbData.carAccident"
          helpText={t(
            'Αυτοκινητιστικό ατύχημα (πρόσκρουση) που ίσως έχει επηρεάσει το πλαίσιο της μπαταρίας',
          )}
        />
        <FieldCheckBox
          disabled={fieldDisabled['xrbData.fire']}
          as={FormCol6}
          label={t('Φωτιά')}
          name="xrbData.fire"
          helpText={t('-	Φωτιά (στο αυτοκίνητο ή πολύ κοντά σε αυτό) ')}
        />
      </FormRow>
      <FormRow>
        <FieldCheckBox
          disabled={fieldDisabled['xrbData.naturalDisasters']}
          as={FormCol6}
          label={t('Φυσικές καταστροφές')}
          name="xrbData.naturalDisasters"
          helpText={t('Φυσικές καταστροφές (πχ: πλημμύρα) ')}
        />
        <FieldCheckBox
          disabled={fieldDisabled['xrbData.vandalism']}
          as={FormCol6}
          label={t('Βανδαλισμός')}
          name="xrbData.vandalism"
          helpText={t('Βανδαλισμός που επηρέασε το πλαίσιο της μπαταρίας (μηχανική καταπόνηση)')}
        />
      </FormRow>
      <FormRow>
        <FieldCheckBox
          disabled={fieldDisabled['xrbData.batteryLiquidCoolingSystemFailure']}
          as={FormCol6}
          label={t('Φθορά απο υπερθέρμανση/υγρασία')}
          name="xrbData.batteryLiquidCoolingSystemFailure"
          helpText={t(
            'Πρόκληση φθοράς (υπερθέρμανση ή υγρασία) στη μπαταρία από αστοχία/ βλάβη στο σύστημα ψύξης (battery liquid cooling system)',
          )}
        />
      </FormRow>
      <hr />
      <FormRow>
        <FieldCheckBox
          disabled={fieldDisabled['xrbData.noBmsData']}
          as={FormCol6}
          label={t('Δεν υπάρχουν αναλυτικά δεδομένα από BMS')}
          name="xrbData.noBmsData"
          helpText={`Αδυναμία/ βλάβη λήψης αναλυτικών δεδομένων από το BMS (Battery Management System) ή άλλη διάταξη/ εργαλείο διάγνωσης`}
        />
        <FieldCheckBox
          disabled={fieldDisabled['xrbData.fireSmokeSignsSigns']}
          as={FormCol6}
          label={t('Σημάδια ζημιάς από φωτιά ή καπνό')}
          name="xrbData.fireSmokeSignsSigns"
          helpText={`Η μπαταρία παρουσιάζει σημάδια ζημιάς ή καταπόνησης από φωτιά ή καπνό που εμφάνισε στο εσωτερικό της`}
        />
      </FormRow>
      <FormRow>
        <FieldCheckBox
          disabled={fieldDisabled['xrbData.oxidationSigns']}
          as={FormCol6}
          label={t('Σημάδια οξείδωσης')}
          name="xrbData.oxidationSigns"
          helpText={`Η μπαταρία παρουσιάζει σημάδια οξείδωσης (πχ: λόγω συνθηκών υψηλής υγρασίας, επαφής με το νερό ή άλλο υγρό)`}
        />
        <FieldCheckBox
          disabled={fieldDisabled['xrbData.leakageVentingSigns']}
          as={FormCol6}
          label={t('Σημάδια διαρροής ή εξαέρωσης')}
          name="xrbData.leakageVentingSigns"
          helpText={`Η μπαταρία παρουσιάζει σημάδια διαρροής ή εξαέρωσης`}
        />
      </FormRow>
      <FormRow>
        <FieldCheckBox
          disabled={fieldDisabled['xrbData.cannotDiagnose']}
          as={FormCol6}
          label={t('Δεν μπορεί να διαγνωστεί η κατάσταση')}
          name="xrbData.cannotDiagnose"
          helpText={`Δεν μπορεί να διαγνωστεί η κατάσταση της μπαταρίας πριν από τη μεταφορά (ούτε μέσω BMS ή αντίστοιχο διαγνωστικό σύστημα, ούτε με άλλα όργανα μέτρησης και πρωτόκολλα αξιολόγησης) `}
        />
        <FieldCheckBox
          disabled={fieldDisabled['xrbData.safetyDeviceTriggered']}
          as={FormCol6}
          label={t('Ένδειξη βλάβης ή ενεργοποίησης διάταξης ασφαλείας')}
          name="xrbData.safetyDeviceTriggered"
          helpText={`Υφίσταται ένδειξη/ καταγραφή βλάβης ή ενεργοποίησης διάταξης ασφαλείας της μπαταρίας (περιοριστής ρεύματος, ασφάλεια, διακοπή ταχείας φόρτισης / αποφόρτισης, κ.α)`}
        />
      </FormRow>
      <FormRow>
        <FieldInput
          as={FormCol6}
          label={t('Άλλο στοιχείο ελέγχου / Πρόσθετες παρατηρήσεις')}
          name="xrbData.additionalObservations"
          placeholder=""
          disabled={fieldDisabled['xrbData.additionalObservations']}
        />
      </FormRow>
      <FormRow>
        <FieldSelectTechnicianAssessment
          as={FormCol6}
          name="xrbData.technicianAssessment"
          label={t('Αξιολόγηση της κατάστασης μπαταρίας από τον Υπεύθυνο Εγκατάστασης')}
          helpText={
            <>
              Κατηγοριοποίηση βάσει Ειδικών διατάξεων της ADR για διενέργεια αξιολόγησης. -{' '}
              <strong>Used</strong> (χρησιμοποιημένη, χωρίς σημάδια φθοράς) -{' '}
              <strong>Damaged</strong> (ελαττωματική, κατεστραμμένη) -{' '}
              <strong>Critically damaged</strong> (Κρίσιμα κατεστραμμένη, αναγνωρισμένη ως
              κατεστραμμένη ή ελαττωματική και επιρρεπής σε ταχεία αποδόμηση, επικίνδυνη αντίδραση,
              παραγωγή φλόγας ή επικίνδυνη εκπομπή θερμότητας, τοξικών, διαβρωτικών ή εύφλεκτων
              αερίων ή ατμών)
            </>
          }
          disabled={fieldDisabled['xrbData.technicianAssessment']}
        />
        <FieldBooleanTriState
          disabled={fieldDisabled['xrbData.packagingForCriticallyDamaged']}
          as={FormCol6}
          label={t('Συσκευασία για critically damaged')}
          name="xrbData.packagingForCriticallyDamaged"
          helpText={`Εάν η μπαταρίας είναι «critically damaged» και το σημείο συλλογής διαθέτει την κατάλληλη P911/LP906 συσκευασίας, επισυνάπτεται το πιστοποιητικού/ έγκρισης καταλληλότητας που φέρε η συσκευασία.`}
        />
      </FormRow>

      {/* Γ. Πρόσβαση για παραλαβή/φόρτωση */}
      <h5 className="form-group-title mt-3 mb-2">{t('Γ. Πρόσβαση για παραλαβή/φόρτωση')}</h5>
      <FormRow>
        <FieldCheckBox
          disabled={fieldDisabled['xrbData.easyLoadingAccess']}
          as={FormCol6}
          label={t('Εξασφαλίζεται εύκολη πρόσβαση φόρτωσης')}
          name="xrbData.easyLoadingAccess"
          helpText={`Εξασφαλίζεται εύκολη πρόσβαση φόρτωσης στο σημείο προσωρινής αποθήκευσης της μπαταρίας (χώρος απαλλαγμένος από ράμπες/ έντονες κλίσεις, χαμηλά ύψη, κλπ);`}
        />
        <FieldCheckBox
          disabled={fieldDisabled['xrbData.specialEquipmentRequired']}
          as={FormCol6}
          label={t('Απαιτούνται ειδικά μηχανικά ή ανυψωτικά μέσα')}
          name="xrbData.specialEquipmentRequired"
          helpText={`Απαιτούνται ειδικά μηχανικά ή ανυψωτικά μέσα για τη φόρτωση της συσκευασίας στο μεταφορικό όχημα (π.χ. mini γερανός);`}
        />
      </FormRow>
      <FormRow>
        <FieldCheckBox
          disabled={fieldDisabled['xrbData.collectionPointHasEquipment']}
          as={FormCol6}
          label={t('Διαθέτει το σημείο συλλογής ειδικά μέσα')}
          name="xrbData.collectionPointHasEquipment"
          helpText={`Διαθέτει το σημείο συλλογής ειδικά μέσα μεταφοράς/ φόρτωσης της συσκευασίας όπως τροχήλατο ανυψωτικό γερανό, κλαρκ, κ.α.;`}
        />

        {(values as any)?.xrbData?.collectionPointHasEquipment && (
          <FieldInput
            as={FormCol6}
            label={t('Περιγραφή διαθέσιμου εξοπλισμού')}
            name="xrbData.equipmentDescription"
            placeholder=""
            disabled={fieldDisabled['xrbData.equipmentDescription']}
          />
        )}
      </FormRow>
      <FormRow>
        <FieldInput
          as={FormCol6}
          label={t('Άλλο στοιχείο επισήμανσης για πρόσβαση')}
          name="xrbData.otherAccessNotes"
          placeholder=""
          disabled={fieldDisabled['xrbData.otherAccessNotes']}
          helpText={`Άλλο στοιχείο επισήμανσης για την πρόσβαση και την απαίτηση ειδικών ενεργειών.`}
        />
      </FormRow>

      <FormRow>
        <FormCol6>
          <FileRepoFieldList
            label="Φωτογραφία(-ιες) της πρόσβασης (σημείο φόρτωσης)"
            fieldName="accessPhotoFileId"
            arrayFieldPath="xrbData.fileDetails"
            disabled={fieldDisabled['xrbData.accessPhotoFileDetails']}
            requiredErrorPath="xrbData.fileDetailsRequired.accessPhotoFileId"
          />
        </FormCol6>
      </FormRow>

      {/* Δ. Φωτογραφικό υλικό */}
      <h5 className="form-group-title mt-3 mb-2">{t('Δ. Φωτογραφικό υλικό')}</h5>
      <FormRow>
        <FormCol6>
          <FileRepoFieldList
            label="Φωτογραφία(-ιες) μπαταρίας"
            fieldName="batteryPhotoFileId"
            arrayFieldPath="xrbData.fileDetails"
            disabled={fieldDisabled['xrbData.batteryPhotoFileDetails']}
            requiredErrorPath="xrbData.fileDetailsRequired.batteryPhotoFileId"
          />
        </FormCol6>
        <FormCol6>
          <FileRepoFieldList
            label="Φωτογραφία(-ιες) συσκευασίας"
            fieldName="packagingPhotoFileId"
            arrayFieldPath="xrbData.fileDetails"
            disabled={fieldDisabled['xrbData.packagingPhotoFileDetails']}
            requiredErrorPath="xrbData.fileDetailsRequired.packagingPhotoFileId"
          />
        </FormCol6>
      </FormRow>

      <FormRow>
        <FormCol6>
          <FileRepoFieldList
            label="Φωτογραφία(-ιες) ετικετών και σήμανσης"
            fieldName="labelsPhotoFileId"
            arrayFieldPath="xrbData.fileDetails"
            disabled={fieldDisabled['xrbData.labelsPhotoFileDetails']}
            requiredErrorPath="xrbData.fileDetailsRequired.labelsPhotoFileId"
          />
        </FormCol6>
      </FormRow>

      {/* Υπεύθυνη Δήλωση Τεχνίτη Υψηλής Τάσης κατ. 2 */}
      <div style={{ minHeight: '2rem' }}> </div>
      <FormRow className="mt-4 text-center">
        <FormCol12 className="text-center">
          <div className="mb-2 fw-semibold" style={{ fontSize: '1.2rem' }}>
            {t('Υπεύθυνη Δήλωση Τεχνίτη Υψηλής Τάσης κατ. 2')}
          </div>
          <div
            className="mb-2 fw-semibold"
            style={{ fontSize: '0.9rem', marginLeft: '10%', marginRight: '10%' }}
          >
            {t(
              'Ως Τεχνίτης Υψηλής Τάσης Κατηγορίας 2 και διενεργών της αξιολόγησης της κατάστασης της μπαταρίας, βεβαιώνω ότι τα στοιχεία του αιτήματος είναι αληθή και ορθά.',
            )}
          </div>
          <div className={'mt-4 mb-4'} style={{ width: 'fit-content', margin: '0 auto' }}>
            <FieldTickBox
              label={t('Βεβαιώνω')}
              name="xrbData.technicianVerifiesData"
              disabled={fieldDisabled['xrbData.technicianVerifiesData']}
              underline
            />
          </div>
          <FormRow>
            <FieldInput
              as={FormCol6}
              label={t('Ονοματεπώνυμο Τεχνίτη Υψηλής Τάσης')}
              name="xrbData.technicianName"
              disabled={fieldDisabled['xrbData.technicianName']}
            />
            <FieldInput
              as={FormCol6}
              label={t('Α.Μ. Τεχνίτη Υψηλής Τάσης')}
              name="xrbData.technicianRegistrationNumber"
              disabled={fieldDisabled['xrbData.technicianRegistrationNumber']}
            />
          </FormRow>
        </FormCol12>
      </FormRow>
    </div>
  );
});
